import router from '@/router';
import { STORAGE_KEYS } from '@/services/constant';
import { clearSessionKeys } from '@/services/dataService';
import httpClient from '@/services/httpClient';
import store from '@/store';
import Keycloak from 'keycloak-js';
import { reactive } from 'vue';

const state = reactive({
  keycloak: null,
  customToken: null,
  tokenRefreshTimeout: null,
  isRefreshing: false,
  refreshPromise: null,
  keycloakConfig: {
    url: process.env.VUE_APP_KEYCLOAK_URL,
    realm: process.env.VUE_APP_KEYCLOAK_REALM,
    clientId: process.env.VUE_APP_KEYCLOAK_CLIENTID,
  },
});

const REFRESH_BUFFER = 60_000;
const REFRESH_LOCK_KEY = 'auth_refresh_lock';
const LOCK_TTL = 10_000;
const expiryCache = new Map();
const MAX_EXPIRY_CACHE = 20;

const keycloakService = {
  async init() {
    state.keycloak = new Keycloak(state.keycloakConfig);

    window.addEventListener('storage', event => {
      if (event.key === STORAGE_KEYS.CUSTOM_TOKEN) {
        if (event.newValue) {
          try {
            state.customToken = JSON.parse(event.newValue);
          } catch {
            state.customToken = null;
          }
          if (state.customToken) this.scheduleTokenRefresh();
        } else {
          this.handleSessionEnded();
        }
      }
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.isLoggedIn()) {
        this.scheduleTokenRefresh();
      }
    });

    try {
      const authenticated = await state.keycloak.init({
        onLoad: 'check-sso',
        flow: 'hybrid',
        checkLoginIframe: false,
      });

      if (authenticated && state.keycloak?.token) {
        this.setCustomToken({
          access_token: state.keycloak.token,
          refresh_token: state.keycloak.refreshToken,
        });
      }
      if (this.isLoggedIn()) {
        store.commit('setLoggedIn');
        this.scheduleTokenRefresh();
      }
      if (authenticated && state.keycloak) {
        state.keycloak.onTokenExpired = () => this.refreshToken();
      }
      return authenticated;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Keycloak Init Error:', error);
      }
      return false;
    }
  },

  async loginWithEmail(credentials) {
    this._clearLocalSession();
    try {
      const { data } = await httpClient.post('accounts/login/', credentials, { skipAuth: true });
      if (data?.token) {
        this.setCustomToken(data.token);
        store.commit('setLoggedIn');
        this.scheduleTokenRefresh();
      }
      return data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  loginWithSocial(provider) {
    return state.keycloak.login({ idpHint: provider });
  },

  getTokenExpiry(token) {
    if (!token || typeof token !== 'string') return null;
    if (expiryCache.has(token)) return expiryCache.get(token);
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const payload = JSON.parse(atob(parts[1]));
      const expiry = payload.exp ? payload.exp * 1000 : null;
      if (expiryCache.size >= MAX_EXPIRY_CACHE) {
        expiryCache.delete(expiryCache.keys().next().value);
      }
      expiryCache.set(token, expiry);
      return expiry;
    } catch {
      return null;
    }
  },

  scheduleTokenRefresh() {
    if (state.tokenRefreshTimeout) {
      clearTimeout(state.tokenRefreshTimeout);
      state.tokenRefreshTimeout = null;
    }
    const accessToken = this._getBestAccessToken();
    if (!accessToken) return;
    const expiryTime = this.getTokenExpiry(accessToken);
    if (!expiryTime) return;
    const targetRefreshTime = expiryTime - REFRESH_BUFFER;
    const delay = targetRefreshTime - Date.now();
    localStorage.setItem(STORAGE_KEYS.TOKEN_REFRESH, targetRefreshTime.toString());
    if (delay <= 0) {
      this.refreshToken();
    } else {
      state.tokenRefreshTimeout = setTimeout(() => this.refreshToken(), delay);
    }
  },

  async refreshToken() {
    if (state.isRefreshing) return state.refreshPromise;
    const lock = localStorage.getItem(REFRESH_LOCK_KEY);
    if (lock && Date.now() - parseInt(lock, 10) < LOCK_TTL) {
      return new Promise(resolve => {
        setTimeout(async () => {
          resolve(await this.refreshToken());
        }, LOCK_TTL / 5);
      });
    }
    const latestRaw = localStorage.getItem(STORAGE_KEYS.CUSTOM_TOKEN);
    const currentToken = this.getCustomToken();
    if (latestRaw && currentToken) {
      try {
        const latestToken = JSON.parse(latestRaw);
        if (latestToken.access_token !== currentToken.access_token) {
          const latestExpiry = this.getTokenExpiry(latestToken.access_token);
          if (latestExpiry && latestExpiry - Date.now() > REFRESH_BUFFER) {
            state.customToken = latestToken;
            this.scheduleTokenRefresh();
            return true;
          }
        }
      } catch {
        // Silently fail
      }
    }
    localStorage.setItem(REFRESH_LOCK_KEY, Date.now().toString());
    state.isRefreshing = true;
    state.refreshPromise = (async () => {
      try {
        let success = false;
        if (currentToken) {
          success = await this.refreshCustomToken();
        }
        if (!success && state.keycloak?.authenticated) {
          success = await this.refreshKeycloakToken();
        }
        if (success) {
          this.scheduleTokenRefresh();
        } else {
          await this.logout();
        }
        return success;
      } finally {
        state.isRefreshing = false;
        state.refreshPromise = null;
      }
    })();
    return state.refreshPromise;
  },

  async refreshCustomToken() {
    const token = this.getCustomToken();
    if (!token?.refresh_token) return false;
    try {
      const { data } = await httpClient.post(
        'accounts/refresh_token/',
        { refresh_token: token.refresh_token },
        { headers: { Authorization: `Bearer ${token.access_token}` }, skipAuth: true }
      );
      if (data?.token) {
        this.setCustomToken(data.token);
        return true;
      }
      return false;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Custom token refresh failed:', error);
      }
      return false;
    }
  },

  async refreshKeycloakToken() {
    if (!state.keycloak) return false;
    try {
      const refreshed = await state.keycloak.updateToken(70);
      if (refreshed && state.keycloak.token) {
        this.setCustomToken({
          access_token: state.keycloak.token,
          refresh_token: state.keycloak.refreshToken,
        });
      }
      return refreshed !== false;
    } catch {
      return false;
    }
  },

  setCustomToken(token) {
    state.customToken = token;
    localStorage.setItem(STORAGE_KEYS.CUSTOM_TOKEN, JSON.stringify(token));
    const expiry = this.getTokenExpiry(token.access_token);
    if (expiry) {
      localStorage.setItem(STORAGE_KEYS.TOKEN_REFRESH, (expiry - REFRESH_BUFFER).toString());
    }
  },

  getCustomToken() {
    if (!state.customToken) {
      const stored = localStorage.getItem(STORAGE_KEYS.CUSTOM_TOKEN);
      if (stored) {
        try {
          state.customToken = JSON.parse(stored);
        } catch {
          state.customToken = null;
        }
      }
    }
    return state.customToken;
  },

  isLoggedIn() {
    const token = this.getCustomToken();
    if (token?.access_token) {
      const expiry = this.getTokenExpiry(token.access_token);
      if (expiry && Date.now() < expiry) return true;
    }
    return !!state.keycloak?.authenticated;
  },

  async getToken() {
    const customToken = this.getCustomToken();
    if (customToken?.access_token) {
      const expiry = this.getTokenExpiry(customToken.access_token);
      if (expiry && Date.now() < expiry) {
        return customToken.access_token;
      }
      if (state.isRefreshing) {
        await state.refreshPromise;
        return this.getCustomToken()?.access_token ?? null;
      }
      const success = await this.refreshToken();
      return success ? (this.getCustomToken()?.access_token ?? null) : null;
    }
    if (state.keycloak?.authenticated) {
      try {
        await state.keycloak.updateToken(30);
        if (state.keycloak.token) {
          this.setCustomToken({
            access_token: state.keycloak.token,
            refresh_token: state.keycloak.refreshToken,
          });
        }
        return state.keycloak.token ?? null;
      } catch {
        await this.logout();
        return null;
      }
    }
    return null;
  },

  async logout() {
    if (store.state.isLoggedOut) return;
    this._clearLocalSession();
    try {
      await store.dispatch('logout');
      if (state.keycloak?.authenticated) {
        await state.keycloak.logout({ redirectUri: window.location.origin + '/login' });
      } else {
        this.handleSessionEnded();
      }
    } catch {
      this.handleSessionEnded();
    }
  },

  handleSessionEnded() {
    if (router.currentRoute.value.path !== '/login') {
      router.push('/login').catch(() => {});
    }
  },

  _clearLocalSession() {
    if (state.tokenRefreshTimeout) {
      clearTimeout(state.tokenRefreshTimeout);
      state.tokenRefreshTimeout = null;
    }
    state.customToken = null;
    expiryCache.clear();
    localStorage.removeItem(STORAGE_KEYS.CUSTOM_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_REFRESH);
    localStorage.removeItem(REFRESH_LOCK_KEY);
    clearSessionKeys();
  },

  _getBestAccessToken() {
    const token = this.getCustomToken();
    if (token?.access_token) {
      const expiry = this.getTokenExpiry(token.access_token);
      if (expiry && Date.now() < expiry) return token.access_token;
    }
    return state.keycloak?.token ?? null;
  },
};

export default keycloakService;
